import React from 'react';
import Form from 'react-bootstrap/Form';
import { Col, Container, FormGroup, Row } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import { useState, useEffect, useRef } from 'react';
import Spinner from 'react-bootstrap/Spinner';
import { PlanDeleteGroups, RemoveGroupMembers, FinalizeDeleteGroups } from '../../../functions/ZIA'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faTrash, faCopy } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Badge from 'react-bootstrap/Badge';
import ProgressBar from 'react-bootstrap/ProgressBar';
import { objectToOperationString } from '../../../functions/misc';
import Multiselect from 'multiselect-react-dropdown';

library.add(faTrash, faCopy)

const CHUNK_SIZE = 10; // members removed per worker request (smaller = more frequent progress updates)
const GROUP_CHUNK_SIZE = 5; // groups deleted per worker request (smaller = more frequent progress updates)

function DeleteGroupTab({ groups, verified, showVerifyWindow, setShowVerifyWindow, setVerifyOperation, setVerified, selectedTab }) {
    const [isLoading, setLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState("Deleting groups...")
    const [progress, setProgress] = useState(0);
    const [tenant, setTenant] = useState("Rheintec");
    const [selected, setSelected] = useState([]);
    const [outputConsole, setOutputConsole] = useState("");
    const [deletedGroups, setDeletedGroups] = useState([]);
    const [failedGroups, setFailedGroups] = useState([]);
    const textareaRef = useRef();
    const [validated, setValidated] = useState(false);

    const multiselectRef = useRef();
    const sleep = ms => new Promise(r => setTimeout(r, ms));

    function evaluateSelect(selectedList, selectedItem) {
        if (selectedItem.name === "Select All") {
            setSelected([{ name: "Select All" }, ...groups[tenant].map((groupName) => ({ name: groupName, id: groupName }))]);
        } else {
            setSelected(selectedList);
        }
    }

    function evaluateRemove(selectedList, selectedItem) {
        if (selectedItem.name === "Select All") {
            setSelected([]);
            return;
        }
        setSelected(selectedList);
    }

    function selectedGroupNames() {
        return selected.filter((g) => g.name !== "Select All").map((g) => g.name);
    }

    function copyList(list) {
        navigator.clipboard.writeText(ListToText(list));
    }

    function ListToText(list) {
        let text = "";
        for (let i = 0; i < list.length; i++) {
            text += i === 0 ? list[i] : "\n" + list[i];
        }
        return text;
    }

    function appendConsole(text) {
        setOutputConsole(prevOutput => prevOutput + text);
    }
    function appendConsoleSeperatorMain(skipNewLine) {
        const width = textareaRef.current.clientWidth;
        const dashes = ('-'.repeat(Math.floor(width / 8)));
        setOutputConsole(prevOutput => prevOutput + (skipNewLine ? "" : "\n") + dashes);
    }
    function appendConsoleSeperatorSecondary() {
        const width = textareaRef.current.clientWidth;
        const dashes = ('-'.repeat(Math.floor(width / 8 / 2)));
        setOutputConsole(prevOutput => prevOutput + "\n" + "\t" + dashes);
    }

    function outputResponseGroupStatus(deleted, failed, total, removedMembers, failedMembers, memberFailureReasons = []) {
        setOutputConsole("");
        appendConsoleSeperatorMain(true);
        appendConsole(`\nRemoved ${removedMembers} membership(s)` + (failedMembers > 0 ? `, ${failedMembers} failed` : ""));
        for (const reason of memberFailureReasons) {
            appendConsole(`\n\tMembership failure: ${reason}`);
        }
        appendConsoleSeperatorSecondary();
        appendConsoleSeperatorMain();
        appendConsole(`\nDeleted ${deleted.length}/${total} groups:`);
        if (deleted.length === 0) {
            appendConsoleSeperatorSecondary();
            appendConsole("\n\tNo groups deleted :(")
        }
        for (const group of deleted) {
            appendConsoleSeperatorSecondary();
            appendConsole(`\n\tGroup: ${group}`);
        }
        appendConsoleSeperatorSecondary();
        appendConsoleSeperatorMain();

        appendConsole(`\nFailed ${failed.length}/${total} groups:`);
        if (failed.length === 0) {
            appendConsoleSeperatorSecondary();
            appendConsole("\n\tNo groups failed to delete :)")
        }
        for (const fail of failed) {
            appendConsoleSeperatorSecondary();
            appendConsole(`\n\tGroup: ${fail.group} \n\tReason: ${fail.message}`);
        }
        appendConsoleSeperatorSecondary();
        appendConsoleSeperatorMain();
    }

    async function handleSubmit(event) {
        event.preventDefault();
        const groupsToDelete = selectedGroupNames();
        if (!verified) {
            try {
                const obj = {
                    "Tenant": tenant,
                    "Operation": "Delete groups",
                    "Affected Resources": { "Groups": groupsToDelete }
                }
                setVerifyOperation(objectToOperationString(obj));
                setShowVerifyWindow(true);
            } catch (error) {
                console.log(error)
            }
            return
        }
        setShowVerifyWindow(false)
        setOutputConsole("");
        setDeletedGroups([]);
        setFailedGroups([]);
        setProgress(0);
        setLoading(true);

        try {
            // Phase 1: resolve groups + find members
            setLoadingMessage("Resolving groups & finding members...");
            const planResponse = await PlanDeleteGroups(groupsToDelete, tenant);
            const plan = await planResponse.json();
            if (!planResponse.ok) {
                throw new Error(plan.message || "Failed to plan deletion");
            }
            const planGroups = plan.groups || [];
            const notFound = plan.notFound || [];
            const memberIds = plan.memberIds || [];
            const groupIds = planGroups.map(g => g.id);

            // Phase 2: remove members in chunks (live progress)
            let removedMembers = 0;
            let failedMembers = 0;
            const memberFailureReasons = new Set();
            for (let i = 0; i < memberIds.length; i += CHUNK_SIZE) {
                const chunk = memberIds.slice(i, i + CHUNK_SIZE);
                setLoadingMessage(`Removing memberships (${i}/${memberIds.length})...`);
                const removeResponse = await RemoveGroupMembers(chunk, groupIds, tenant);
                const removeData = await removeResponse.json();
                // A failed chunk (e.g. auth error) has no `results` array -- surface it
                // instead of silently moving on to delete groups that still have members.
                if (!removeResponse.ok) {
                    throw new Error(removeData.message || `Failed to remove memberships (HTTP ${removeResponse.status})`);
                }
                for (const r of (removeData.results || [])) {
                    if (r.ok) removedMembers++;
                    else { failedMembers++; if (r.message) memberFailureReasons.add(r.message); }
                }
                setProgress(Math.round(((i + chunk.length) / Math.max(memberIds.length, 1)) * 50));
            }
            setProgress(50);

            // Phase 3: activate the member removals, then delete the groups in chunks
            const results = [];
            for (let i = 0; i < planGroups.length; i += GROUP_CHUNK_SIZE) {
                const chunk = planGroups.slice(i, i + GROUP_CHUNK_SIZE);
                setLoadingMessage(`Deleting groups (${i}/${planGroups.length})...`);
                const finalizeResponse = await FinalizeDeleteGroups(
                    chunk,
                    removedMembers,
                    tenant,
                    i === 0,                                       // activate member removals before the 1st chunk
                    i + GROUP_CHUNK_SIZE >= planGroups.length      // activate deletes after the last chunk
                );
                const finalize = await finalizeResponse.json();
                results.push(...(finalize.results || []));
                setProgress(50 + Math.round(((i + chunk.length) / Math.max(planGroups.length, 1)) * 50));
            }
            setProgress(100);

            const deleted = results.filter(r => r.status === "deleted").map(r => r.group);
            const failed = results
                .filter(r => r.status !== "deleted")
                .map(r => ({ group: r.group, message: r.message ?? "Unknown error" }));
            for (const nf of notFound) {
                failed.push({ group: nf, message: "Group not found" });
            }

            setDeletedGroups(deleted);
            setFailedGroups(failed);
            outputResponseGroupStatus(deleted, failed, groupsToDelete.length, removedMembers, failedMembers, [...memberFailureReasons]);
        } catch (error) {
            console.log(error);
            const failed = groupsToDelete.map(g => ({ group: g, message: error.message }));
            setFailedGroups(failed);
            outputResponseGroupStatus([], failed, groupsToDelete.length, 0, 0);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (selectedTab === "deleteGroup" && verified) {
            handleSubmit(new Event("submit"))
        }
        setVerified(false);
    }, [verified])

    const scrollToBottom = () => {
        textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
    };
    useEffect(() => {
        scrollToBottom();
    }, [outputConsole])

    useEffect(() => {
        setSelected([])
    }, [tenant])

    return (
        <Container className=' pt-4 px-5 text-white pb-6'>
            <Row>
                <Col>
                    <Form validated={validated} onSubmit={handleSubmit}>
                        <FormGroup controlId="company">
                            <Form.Label>
                                Select tenant
                            </Form.Label>
                            <Form.Select aria-label="Default select example" value={tenant} onChange={(e) => setTenant(e.target.value)} >
                                {
                                    Object.keys(groups).map((key, index) => (
                                        <option key={index} value={key}>{key}</option>
                                    ))

                                }
                            </Form.Select>
                        </FormGroup>

                        <FormGroup controlId="groupName" className='mt-4'>
                            <Form.Label>
                                {groups[tenant].length === 0 ? <Spinner
                                    variant='light'
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                /> : <></>}
                                &nbsp;&nbsp;Groups to delete
                            </Form.Label>
                            <Multiselect
                                key={`${tenant}-${groups[tenant].length}`}
                                options={groups[tenant].length > 0 ? [{ name: "Select All" }, ...groups[tenant].map(groupName => ({ name: groupName, id: groupName }))] : []}
                                selectedValues={selected}
                                displayValue="name"
                                onSelect={(selectedList, selectedItem) => evaluateSelect(selectedList, selectedItem)}
                                onRemove={(selectedList, selectedItem) => evaluateRemove(selectedList, selectedItem)}
                                ref={multiselectRef}
                                avoidHighlightFirstOption="true"
                                placeholder="Select Groups"
                                showCheckbox="true"
                                showArrow="true"
                                customArrow={<FontAwesomeIcon icon="fa-solid fa-angle-down" style={{ color: "#c6cbce", }} size='sm' />}

                            />
                        </FormGroup>

                        <Button className='mt-4 text-light' variant="danger" type='submit' disabled={isLoading || selectedGroupNames().length === 0} >
                            {isLoading ?
                                <span>
                                    <Spinner
                                        variant='light'
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                    />
                                    {"  " + loadingMessage}
                                </span> :
                                <span>
                                    <FontAwesomeIcon className='me-2' icon="fa-solid fa-trash" style={{ color: "#ffffff", }} />
                                    Delete Groups
                                </span>}
                        </Button>
                    </Form>
                </Col>
            </Row>
            <Row className='mt-5 '>
                <Col>
                    <ProgressBar variant='danger' animated={isLoading} striped={isLoading} now={isLoading || progress > 0 ? progress : 0} label={isLoading || progress > 0 ? `${progress}%` : ""} />
                </Col>
                <Col md="auto" className='text-end'>
                    <Button disabled={deletedGroups.length > 0 ? false : true} onClick={(event) => { copyList(deletedGroups) }} className='rounded-0 pt-0 pb-0 pm-1' size='sm' variant={deletedGroups.length > 0 ? 'success' : 'primary'}>
                        <FontAwesomeIcon className='me-1' size='lg' icon="fa-solid fa-copy" style={{ color: "#ffffff", }} />
                        Copy deleted groups
                        <Badge className='ms-1 ' bg="dark">{deletedGroups.length}</Badge>
                    </Button>
                    <Button disabled={failedGroups.length > 0 ? false : true} onClick={(event) => { copyList(failedGroups.map(fail => fail.group)) }} className='ms-1 rounded-0 pt-0 pb-0 pm-1 text-light' size='sm' variant={failedGroups.length > 0 ? 'danger' : 'primary'}>
                        <FontAwesomeIcon className='me-1' size='lg' icon="fa-solid fa-copy" style={{ color: "#ffffff", }} />
                        Copy failed groups
                        <Badge className='ms-1' bg="dark">{failedGroups.length}</Badge>
                    </Button>
                </Col>
            </Row>
            <Row className='mt-0 text-center'>
                <Col className=''>
                    <textarea value={outputConsole} className='w-100 bg-dark-subtle  text-rheintec-blue' rows="20" readOnly ref={textareaRef}>
                    </textarea>
                </Col>

            </Row>

        </Container >

    );

}

export default DeleteGroupTab;
